import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { getUserByPhone, sendSMS, sendWhatsApp, sendEmail, loadApiSettings, updateUsersWithEmailField } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (phone: string) => Promise<{ success: boolean; message: string }>;
  verifyCode: (phone: string, code: string) => Promise<{ success: boolean; user?: User }>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Store verification codes in memory (in production, use Redis or similar)
const verificationCodes = new Map<string, { code: string; expires: number }>();

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load API settings and check if user is logged in from localStorage
    const initializeAuth = async () => {
      try {
        await loadApiSettings();
        console.log('✅ API settings loaded');
        
        // Update users with email field (one-time migration)
        await updateUsersWithEmailField();
        console.log('✅ Users updated with email field');
      } catch (error) {
        console.error('Error loading API settings:', error);
      }

      const savedUser = localStorage.getItem('zikaron_dalia_user');
      console.log('🔍 Checking saved user:', savedUser);
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          console.log('👤 Loaded user:', userData);
          console.log('🔑 Is admin:', userData.isAdmin);
          setUser(userData);
        } catch (error) {
          console.error('❌ Error parsing saved user:', error);
          localStorage.removeItem('zikaron_dalia_user');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (phone: string): Promise<{ success: boolean; message: string }> => {
    try {
      // Check if user exists
      const existingUser = await getUserByPhone(phone);
      if (!existingUser) {
        return { success: false, message: 'המשתמש לא נמצא במערכת' };
      }

      // Generate 6-digit verification code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const expires = Date.now() + 5 * 60 * 1000; // 5 minutes

      // Store code
      verificationCodes.set(phone, { code, expires });

      // Prepare message
      const message = `קוד האימות שלך למערכת זכרון דליה: ${code}`;
      
      // Check user notification preferences
      const notificationPrefs = existingUser.notificationPreferences || { sms: true, calls: true, whatsapp: true, email: false };
      
      let smsSent = false;
      let whatsappSent = false;
      let emailSent = false;
      let sentMethods: string[] = [];

      // Send SMS if enabled
      if (notificationPrefs.sms) {
        smsSent = await sendSMS(phone, message);
        if (smsSent) {
          sentMethods.push('SMS');
        }
      }

      // Send WhatsApp if enabled
      if (notificationPrefs.whatsapp) {
        whatsappSent = await sendWhatsApp(phone, message);
        if (whatsappSent) {
          sentMethods.push('WhatsApp');
        }
      }

      // Send Email if enabled and email exists
      if (notificationPrefs.email && existingUser.email) {
        emailSent = await sendEmail(existingUser.email, 'קוד אימות - זכרון דליה', message);
        if (emailSent) {
          sentMethods.push('אימייל');
        }
      }

      // Check if at least one method succeeded
      if (sentMethods.length === 0) {
        return { success: false, message: 'שגיאה בשליחת הודעת אימות' };
      }

      const methodText = sentMethods.join(' ו-');
      return { success: true, message: `קוד האימות נשלח בהצלחה ב-${methodText}` };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'שגיאה בהתחברות' };
    }
  };

  const verifyCode = async (phone: string, code: string): Promise<{ success: boolean; user?: User }> => {
    try {
      const storedCode = verificationCodes.get(phone);
      
      if (!storedCode) {
        return { success: false };
      }

      if (Date.now() > storedCode.expires) {
        verificationCodes.delete(phone);
        return { success: false };
      }

      if (storedCode.code !== code) {
        return { success: false };
      }

      // Code is valid, get user data
      const user = await getUserByPhone(phone);
      console.log('🔍 User from database:', user);
      if (!user) {
        return { success: false };
      }

      // Clear the code
      verificationCodes.delete(phone);

      // Save user to localStorage
      localStorage.setItem('zikaron_dalia_user', JSON.stringify(user));
      console.log('💾 Saved user to localStorage:', user);
      console.log('🔑 User is admin:', user.isAdmin);
      setUser(user);

      return { success: true, user };
    } catch (error) {
      console.error('Verification error:', error);
      return { success: false };
    }
  };

  const logout = () => {
    localStorage.removeItem('zikaron_dalia_user');
    setUser(null);
  };

  const isAdmin = user?.isAdmin || false;

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      verifyCode,
      logout,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
